/**
 * Trainer-Slot-Validation Logic (06/2026 Refactor Phase 2).
 *
 * Reine Funktionen für die Verfügbarkeits-Berechnung im Kalender. Alle
 * Abhängigkeiten kommen über Parameter — kein State, keine Hooks.
 *
 * Verwendet von `AppointmentsPage.js`. Die React-Komponente bindet die
 * Funktionen mit ihrem aktuellen State (Vacations/Blocks/Exceptions/
 * Appointments/StudioHours/etc.) und ruft sie pro Slot auf.
 */

import { getSwissHM } from '@/components/appointments/calendarUtils';
import { isStudioOpenOnDate } from './calendarDateUtils';

// ────────────────────────────────────────────────────────────────────────
//  Slot-Window-Generator
// ────────────────────────────────────────────────────────────────────────

/**
 * Liefert eine Set<string> aller validen Slot-Start-Uhrzeiten (HH:MM) für
 * einen Trainer an einem Datum. Berücksichtigt:
 *   - Special-Date-Übersteuerung (hat Vorrang vor Weekly-Schedule)
 *   - Slot-Verankerung am Fenster-Start (z.B. Start 17:45 + 30Min →
 *     17:45, 18:15, 18:45 ist valid, NICHT 18:00).
 *   - Robustes Parsing von `slot_duration` (Number oder String wie '30min').
 *   - Density-Aware Override (07/2026): Wenn `densityOverrideStep` gesetzt
 *     ist, wird dieser Wert (in Minuten) statt der Trainer-eigenen
 *     `slot_duration` genutzt. Wird von AppointmentsPage.js verwendet,
 *     um bei vielen Terminen pro Tag ein feineres Raster anzuzeigen.
 */
export const getValidSlotStartsForTrainer = (
  trainerData,
  dateStr,
  dayName,
  densityOverrideStep = null
) => {
  const dayAvail = trainerData?.availability?.[dayName];
  const specialDate = trainerData?.availability?.special_dates?.[dateStr];

  let windows = null;
  let slotDuration = 15;

  const parseDur = (v) => {
    if (typeof v === 'number' && v > 0) return v;
    if (typeof v === 'string') {
      const m = parseInt(v.replace(/[^0-9]/g, ''), 10);
      if (!isNaN(m) && m > 0) return m;
    }
    return null;
  };

  if (specialDate) {
    if (!specialDate.active) return new Set();
    windows = specialDate.slots || [];
    slotDuration =
      parseDur(specialDate.slot_duration) || parseDur(dayAvail?.slot_duration) || 15;
  } else if (dayAvail?.active) {
    windows = dayAvail.slots || [];
    slotDuration = parseDur(dayAvail.slot_duration) || 15;
  } else {
    return new Set();
  }

  // Density-Aware Override: wenn gesetzt UND kleiner als aktueller Trainer-Step
  // → das feinere Raster gewinnt, damit anchor-slots/Lücken sichtbar werden.
  // Wenn größer → Trainer-Step gewinnt (wir wollen nicht künstlich weniger
  // Slots zeigen, wenn der Trainer schon feiner arbeitet).
  if (
    typeof densityOverrideStep === 'number' &&
    densityOverrideStep > 0 &&
    densityOverrideStep < slotDuration
  ) {
    slotDuration = densityOverrideStep;
  }

  const validStarts = new Set();
  windows.forEach((slot) => {
    const fromStr = slot.from_time || slot.from || '09:00';
    const toStr = slot.to_time || slot.to || '18:00';
    const [fromH, fromM] = fromStr.split(':').map(Number);
    const [toH, toM] = toStr.split(':').map(Number);
    const fromMin = fromH * 60 + fromM;
    const toMin = toH * 60 + toM;
    for (let m = fromMin; m + slotDuration <= toMin; m += slotDuration) {
      const hh = Math.floor(m / 60).toString().padStart(2, '0');
      const mm = (m % 60).toString().padStart(2, '0');
      validStarts.add(`${hh}:${mm}`);
    }
  });
  return validStarts;
};

/**
 * Admin-Only-Info für einen Kalender-Slot (06/2026).
 *
 * Ermittelt, ob das Verfügbarkeits-Fenster, in das `timeSlot` (HH:MM) am
 * `dateStr`/`dayName` fällt, aktuell für KUNDEN gesperrt ist (und damit nur
 * Admin/Trainer buchen können). Gründe:
 *   - 'expired'  → slot.valid_until liegt vor dem Kalendertag
 *   - 'not_yet'  → slot.valid_from liegt nach dem Kalendertag
 *   - 'hidden'   → slot.is_customer_visible === false
 *
 * Rückgabe: null (normal kundenbuchbar / kein Fenster) oder
 *   { reason, validFrom, validUntil }.
 */
export const getAdminOnlySlotInfo = (trainerData, dateStr, dayName, timeSlot) => {
  const specialDate = trainerData?.availability?.special_dates?.[dateStr];
  const dayAvail = trainerData?.availability?.[dayName];

  let windows = null;
  if (specialDate) {
    if (!specialDate.active) return null;
    windows = specialDate.slots || [];
  } else if (dayAvail?.active) {
    windows = dayAvail.slots || [];
  } else {
    return null;
  }

  const [th, tm] = timeSlot.split(':').map(Number);
  const tMin = th * 60 + tm;

  for (const slot of windows) {
    const fromStr = slot.from_time || slot.from || '09:00';
    const toStr = slot.to_time || slot.to || '18:00';
    const [fh, fm] = fromStr.split(':').map(Number);
    const [oh, om] = toStr.split(':').map(Number);
    const fromMin = fh * 60 + fm;
    const toMin = oh * 60 + om;
    if (tMin >= fromMin && tMin < toMin) {
      const vf = (slot.valid_from || '').trim ? (slot.valid_from || '').trim() : (slot.valid_from || '');
      const vu = (slot.valid_until || '').trim ? (slot.valid_until || '').trim() : (slot.valid_until || '');
      const hidden = slot.is_customer_visible === false;
      const expired = vu && vu < dateStr;
      const notYet = vf && vf > dateStr;
      if (hidden || expired || notYet) {
        return {
          reason: expired ? 'expired' : notYet ? 'not_yet' : 'hidden',
          validFrom: vf || null,
          validUntil: vu || null,
        };
      }
      return null; // Fenster passt & ist kundenbuchbar
    }
  }
  return null;
};


/**
 * Density-Aware Slot-Step-Berechnung (07/2026).
 *
 * Ermittelt den gewünschten Slot-Step (in Minuten) für einen Trainer an
 * einem Datum basierend auf der Anzahl seiner aktiven Termine.
 *
 * @param {string} trainerId
 * @param {Date} currentDate
 * @param {Array} appointments — komplette Termin-Liste (wird intern gefiltert)
 * @param {Object} densityConfig — { enabled, threshold, lowStep, highStep }
 * @returns {number|null} — Step in Minuten, oder null wenn deaktiviert.
 */
export const getDensityAwareStep = (
  trainerId,
  currentDate,
  appointments = [],
  densityConfig = null
) => {
  if (!densityConfig || !densityConfig.enabled) return null;
  const threshold = densityConfig.threshold || 4;
  const lowStep = densityConfig.lowStep || 30;
  const highStep = densityConfig.highStep || 15;
  const dateStr = currentDate.toISOString().split('T')[0];
  const count = appointments.filter((apt) => {
    if (apt.trainer_id !== trainerId) return false;
    if (['cancelled', 'no_show'].includes(apt.status)) return false;
    const aptDate = new Date(apt.start_time).toISOString().split('T')[0];
    return aptDate === dateStr;
  }).length;
  return count >= threshold ? highStep : lowStep;
};

// ────────────────────────────────────────────────────────────────────────
//  Vacation / Exception / Block Checks
// ────────────────────────────────────────────────────────────────────────

/**
 * Liefert das Vacation-Objekt falls der Trainer am `currentDate` in den
 * Ferien ist, sonst `undefined`.
 */
export const findTrainerVacation = (trainerId, currentDate, trainerVacations = []) => {
  const dateStr = currentDate.toISOString().split('T')[0];
  return trainerVacations.find((vacation) => {
    if (vacation.trainer_id !== trainerId) return false;
    return dateStr >= vacation.start_date && dateStr <= vacation.end_date;
  });
};

/**
 * Findet das Termin-Objekt das einen bestimmten 30-Min-Slot für einen
 * Trainer belegt. Aktive Status: ALLES außer cancelled / no_show.
 */
export const getAppointmentForSlot = (
  trainerId,
  timeSlot,
  appointments = [],
  currentDate
) => {
  return appointments.find((apt) => {
    if (apt.trainer_id !== trainerId) return false;
    const aptStartDate = new Date(apt.start_time);
    if (aptStartDate.toDateString() !== currentDate.toDateString()) return false;

    const [slotHours, slotMinutes] = timeSlot.split(':').map(Number);
    const slotStartTime = slotHours * 60 + slotMinutes;
    const slotEndTime = slotStartTime + 30;

    const sStart = getSwissHM(apt.start_time);
    const sEnd = getSwissHM(apt.end_time);
    const aptStartMinutes = sStart.hour * 60 + sStart.minute;
    const aptEndMinutes = sEnd.hour * 60 + sEnd.minute;

    return aptStartMinutes < slotEndTime && aptEndMinutes > slotStartTime;
  });
};

// ────────────────────────────────────────────────────────────────────────
//  Combined: isTrainerAvailable
// ────────────────────────────────────────────────────────────────────────

/**
 * Gesamt-Checker: Ist ein bestimmter 30-Min-Slot für den Trainer buchbar?
 *
 * Hierarchie (höchste Priorität zuerst):
 *   1. Studio überhaupt offen?
 *   2. Trainer in Ferien?
 *   3. Slot durch aktiven Termin belegt?
 *   4. Slot durch eine Exception (Sonder-Öffnung) freigeschaltet? → frei
 *   5. Slot durch manuellen Block gesperrt? → blockiert
 *   6. Slot Teil der regulären Verfügbarkeit?
 */
// 08/2026: Slot liegt in einem Blocker, der per Ausnahme wieder geöffnet
// wurde → Kalender zeigt ihn dezent schraffiert (Info: "Per Ausnahme geöffnet").
export const isSlotOpenedByException = (trainerData, timeSlot, ctx) => {
  const { currentDate, trainerBlocks = [], trainerExceptions = [] } = ctx;
  const trainerId = trainerData.trainer?.id || trainerData.id;
  const dateStr = currentDate.toISOString().split('T')[0];
  const [h, m] = timeSlot.split(':').map(Number);
  const t = h * 60 + m;

  const inRange = (entry) => {
    const [sH, sM] = (entry.start_time || '00:00').split(':').map(Number);
    const [eH, eM] = (entry.end_time || '23:59').split(':').map(Number);
    return t >= sH * 60 + sM && t < eH * 60 + eM;
  };

  const block = trainerBlocks.find(
    (b) => b.trainer_id === trainerId && b.date === dateStr && inRange(b)
  );
  if (!block) return null;
  const exc = trainerExceptions.find(
    (e) => e.trainer_id === trainerId && e.date === dateStr && inRange(e)
  );
  return exc ? { block, exception: exc } : null;
};

export const isTrainerAvailable = (trainerData, timeSlot, ctx) => {
  const {
    currentDate,
    studioOpeningHours = {},
    trainerVacations = [],
    trainerBlocks = [],
    trainerExceptions = [],
    appointments = [],
    densityConfig = null,
  } = ctx;

  if (!isStudioOpenOnDate(currentDate, studioOpeningHours)) return false;

  const trainerId = trainerData.trainer?.id || trainerData.id;
  const dateStr = currentDate.toISOString().split('T')[0];
  const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

  if (findTrainerVacation(trainerId, currentDate, trainerVacations)) {
    // Ferien: ganztags gesperrt — AUSSER eine Ausnahme (Sonder-Öffnung) gibt
    // dieses Zeitfenster wieder frei ("Slot trotz Ferien öffnen"). Parität zum
    // Backend (compute_availability / is_trainer_available).
    const [vH, vM] = timeSlot.split(':').map(Number);
    const vMin = vH * 60 + vM;
    const openedDuringVacation = trainerExceptions.some((exc) => {
      if (exc.trainer_id !== trainerId || exc.date !== dateStr) return false;
      const [sH, sM] = (exc.start_time || '00:00').split(':').map(Number);
      const [eH, eM] = (exc.end_time || '23:59').split(':').map(Number);
      return vMin >= sH * 60 + sM && vMin < eH * 60 + eM;
    });
    if (!openedDuringVacation) return false;
  }

  // Bereits durch einen aktiven Termin desselben Trainers belegt?
  const blockingAppointment = appointments.find((apt) => {
    if (apt.trainer_id !== trainerId) return false;
    if (apt.status === 'cancelled' || apt.status === 'no_show') return false;
    const aptStartDate = new Date(apt.start_time);
    if (aptStartDate.toDateString() !== currentDate.toDateString()) return false;

    const [slotHours, slotMinutes] = timeSlot.split(':').map(Number);
    const slotTime = slotHours * 60 + slotMinutes;
    const sStart = getSwissHM(apt.start_time);
    const sEnd = getSwissHM(apt.end_time);
    const startTime = sStart.hour * 60 + sStart.minute;
    const endTime = sEnd.hour * 60 + sEnd.minute;
    return slotTime >= startTime && slotTime < endTime;
  });
  if (blockingAppointment) return false;

  const [slotHours, slotMinutes] = timeSlot.split(':').map(Number);
  const slotTimeInMinutes = slotHours * 60 + slotMinutes;

  // Exceptions haben höchste Priorität — überschreiben Blocks und Schedule
  const hasException = trainerExceptions.some((exc) => {
    if (exc.trainer_id !== trainerId || exc.date !== dateStr) return false;
    const [excStartH, excStartM] = (exc.start_time || '00:00').split(':').map(Number);
    const [excEndH, excEndM] = (exc.end_time || '23:59').split(':').map(Number);
    const excStart = excStartH * 60 + excStartM;
    const excEnd = excEndH * 60 + excEndM;
    return slotTimeInMinutes >= excStart && slotTimeInMinutes < excEnd;
  });
  if (hasException) return true;

  const isBlocked = trainerBlocks.some((block) => {
    if (block.trainer_id !== trainerId || block.date !== dateStr) return false;
    const [blockStartH, blockStartM] = (block.start_time || '00:00').split(':').map(Number);
    const [blockEndH, blockEndM] = (block.end_time || '23:59').split(':').map(Number);
    const blockStart = blockStartH * 60 + blockStartM;
    const blockEnd = blockEndH * 60 + blockEndM;
    return slotTimeInMinutes >= blockStart && slotTimeInMinutes < blockEnd;
  });
  if (isBlocked) return false;

  const validSlotStarts = getValidSlotStartsForTrainer(
    trainerData,
    dateStr,
    dayName,
    getDensityAwareStep(trainerId, currentDate, appointments, densityConfig)
  );
  if (validSlotStarts.has(timeSlot)) return true;

  // Anchor-Slot-Check (Iter 339++/07-2026): Wenn ein bestehender Termin
  // exakt bei `timeSlot` endet, ist dies ein valider Anschluss-Slot,
  // selbst wenn er nicht auf dem Trainer-Grid liegt (z.B. 07:15 endet,
  // 07:15 wäre die Grid-Position; ein Termin bis 08:15 macht 08:15 zu
  // einem Anker-Slot obwohl das Grid 08:00 / 08:30 vorsieht).
  const targetMin = slotTimeInMinutes;
  const hasAnchorEnd = appointments.some((apt) => {
    if (apt.trainer_id !== trainerId) return false;
    if (['cancelled', 'no_show'].includes(apt.status)) return false;
    const aptStartDate = new Date(apt.start_time);
    if (aptStartDate.toDateString() !== currentDate.toDateString()) return false;
    const sEnd = getSwissHM(apt.end_time);
    return sEnd.hour * 60 + sEnd.minute === targetMin;
  });
  return hasAnchorEnd;
};

// ────────────────────────────────────────────────────────────────────────
//  Slot-Block-Reason (für Drag&Drop und Booking-Konflikt-Checks)
// ────────────────────────────────────────────────────────────────────────

/**
 * Liefert einen menschenlesbaren Grund warum ein Slot blockiert ist
 * (für Toasts), oder `null` wenn der Slot frei ist. Berücksichtigt:
 *   - Mindbody-Pausen / Kurse / Belegungen (mit Multi-Participant-Logik)
 *   - Lokale Termine + interne Pausen (`is_break`)
 *
 * `opts`:
 *   - maxParticipants: Maximum erlaubte Teilnehmer pro Slot (Default 1)
 *   - occupancy: Wie viele Plätze diese Buchung belegen würde (Default 1)
 */
export const getSlotBlockReason = (
  trainerId,
  newStart,
  newEnd,
  excludeAppointmentIds = [],
  opts = {},
  ctx
) => {
  const maxParticipants = opts.maxParticipants || 1;
  const occupancy = opts.occupancy || 1;
  const { mindbodySlotsByTrainer = {}, appointments = [] } = ctx;

  // 1) Mindbody-Slots
  const mbSlots = mindbodySlotsByTrainer[trainerId] || [];
  let mbCount = 0;
  for (const mb of mbSlots) {
    const s = new Date(mb.start_time);
    const en = new Date(mb.end_time);
    if (s < newEnd && en > newStart) {
      if (mb.source_type === 'unavailability') {
        return 'Dieser Slot ist durch eine Pause blockiert.';
      }
      if (mb.source_type === 'class') {
        return 'Der Trainer unterrichtet hier einen Kurs (Mindbody).';
      }
      mbCount += 1;
    }
  }
  if (mbCount > 0 && mbCount + occupancy > maxParticipants) {
    return `Slot in Mindbody voll belegt (${mbCount} ${mbCount === 1 ? 'Kunde' : 'Kunden'}, max. ${maxParticipants} ${maxParticipants === 1 ? 'Platz' : 'Plätze'}).`;
  }

  // 2) Lokale Termine + interne Pausen
  for (const apt of appointments) {
    if (apt.trainer_id !== trainerId) continue;
    if (excludeAppointmentIds.includes(apt.id)) continue;
    if (['cancelled', 'no_show'].includes(apt.status)) continue;
    const s = new Date(apt.start_time);
    const en = new Date(apt.end_time);
    if (s < newEnd && en > newStart) {
      return apt.is_break
        ? 'Dieser Slot ist durch eine Pause blockiert.'
        : 'Dieser Slot ist bereits belegt.';
    }
  }
  return null;
};
